<template>
  <div @click="hideSearchTip">
    <layout-header />
    <div>
      <router-view />
    </div>
    <imDialog v-if="shouldShowImDialog" />
    <layout-footer v-if="showFooter" />
    <login />
  </div>
</template>

<script>
import LayoutFooter from "./component/layout-footer.vue";
import LayoutHeader from "./component/layout-header.vue";
import imDialog from "./component/imDialog.vue";
import Login from "@/components/Login";
import loadBMap from "@/utils/loadBMap.js";
import Constant from "@/common/Constant";
import tool from "@/utils/tool.js";
import { mapState, mapGetters, mapActions } from "vuex";
import houseServer from "@/services/houseServer"
import { setItem } from '@/utils/storage'
import SessionCache from '@/common/Session';

export default {
  name: "LayoutIndex",

  computed: {
    showFooter() {
      const regex = /map/i;
      return !regex.test(this.$route.path);
    },

    ...mapState(["isAllowPosition", "companyConfig", 'isGetLocation', 'cityCompany', 'location', 'isRefreshPage', 'user']),

    // ✅ 正确的 getter 写法
    ...mapGetters('im', ['hasFriends', 'hasUnreadMessages', 'totalUnreadCount']),

    // 判断用户是否已登录
    isUserLoggedIn() {
      return this.user && this.user.account && this.user.account.guid;
    },

    // 咨询框显示条件（优化版）
    shouldShowImDialog() {
      // 0. 有些路由中的指定页面不显示咨询框
      if (this.$route.meta?.hideIM) {
        return false
      }

      // 1. 必须满足的基本条件：用户已登录且有用户guid
      if (!this.isUserLoggedIn) {
        return false;
      }

      // 2. 只要有好友就显示，不管有没有未读消息
      return this.hasFriends;
    }
  },

  data() {
    return {
      isRefresh: false,
      imInitialized: false, // 标记IM是否已初始化
      imInitTimer: null, // 用于延迟初始化的定时器
    };
  },

  components: {
    LayoutFooter,
    LayoutHeader,
    Login,
    imDialog
  },

  methods: {
    // ✅ 正确的 mapActions 写法 - 对象形式
    ...mapActions({
      // 根级别的 actions
      updateStateAction: 'updateState', // 注意：根级别的 updateState action 可能不存在，下面有本地方法

      // im 模块的 actions
      initIM: 'im/initIM',
      getFriends: 'im/getFriends',
      resetAndLoadFriends: 'im/resetAndLoadFriends',
      loadMoreFriends: 'im/loadMoreFriends',
      ensureFriendAndChat: 'im/ensureFriendAndChat'
    }),

    // ✅ 或者使用数组形式（两种方式都可以）
    /*
    ...mapActions([
      'updateState' // 如果根级别有这个 action
    ]),
    ...mapActions('im', [
      'initIM',
      'getFriends',
      'resetAndLoadFriends',
      'loadMoreFriends',
      'ensureFriendAndChat'
    ]),
    */

    hideSearchTip() {
      // 使用本地的 updateState 方法，而不是 action
      this.updateState({ data: false, key: 'showSearchTip' });
    },

    updateState({ data, key }) {
      // 本地方法，直接调用 commit
      this.$store.commit("updateState", {
        data,
        key
      });
    },

    async loadCompanyConfig() {
      const [err, { data: { logo, prefixName, suffixName, webSiteInfo, citys, tel } }] = await tool.to(houseServer.getCompanyConfig())
      if (err) {
        return
      }
      const _webSiteInfo = !webSiteInfo ? {} : JSON.parse(webSiteInfo)
      const logos = JSON.parse(logo) || {};
      if (citys) {
        const city = JSON.parse(citys)[0]
        const location = {
          cityCode: city.cityCode,
          cityName: city.city,
          latitude: city.lat,
          longitude: city.lng
        }
        this.$store.commit('saveLocation', location)
        this.updateState({ data: location, key: "location" })
      }

      const newWebSiteInfo = {
        ...this.companyConfig.webSiteInfo,
        ..._webSiteInfo
      }
      const companyConfig = {
        ...this.companyConfig,
        ...logos,
        tel,
        firstName: prefixName,
        lastName: suffixName,
        webSiteInfo: newWebSiteInfo,
      }
      this.updateState({ data: companyConfig, key: "companyConfig" })
      this.updateLinkIcon()
    },

    updateLinkIcon(icon = this.companyConfig.greenLogo) {
      const link = document.querySelector("link[rel*='icon']") || document.createElement("link")
      link.rel = "icon"
      link.href = icon
      document.querySelector('head').appendChild(link)
    },

    async setCompanyByCityCode(cityCode = this.location.cityCode) {
      const [err, { data }] = await tool.to(houseServer.getCompanyByCityCode(cityCode))
      if (err) {
        return
      }
      this.$store.commit("updateState", { data: data || {}, key: 'cityCompany' });
      setItem(Constant.CITY_COMPANY, data)
      this.onRefreshPage()
    },

    onRefreshPage() {
      if (!this.isRefreshPage) {
        this.$router.go(0)
        setItem(Constant.IS_REFRESH_PAGE, true)
      }
    },

    loadCurrentLocation() {
      if (Constant.DEFAULT_COMPANY_GUID) {
        this.setCompanyByCityCode()
        return
      }
      setTimeout(() => {
        !this.isGetLocation && tool.getLocation(() => {
          !Object.keys(this.cityCompany).length && this.setCompanyByCityCode()
        })
      }, 500);
    },

    // 初始化IM连接和好友列表
    async initIMConnection() {
      if (this.imInitialized) {
        return;
      }

      if (!this.isUserLoggedIn) {
        return;
      }

      try {
        const token = SessionCache.getAccessToken();
        const userGuid = this.user.account.guid;

        if (!token || !userGuid) {
          return;
        }

        this.imInitialized = true;

        // ✅ 使用 mapActions 映射的 initIM 方法
        await this.initIM({
          account: userGuid,
          token: token,
          callbackv2: async () => {

            setTimeout(async () => {
              try {
                // ✅ 使用 mapActions 映射的 resetAndLoadFriends 方法
                const friends = await this.resetAndLoadFriends();

                // 可选：触发会话列表更新
                this.$store.commit('im/FORCE_REFRESH_CONVERSATIONS');
              } catch (error) {
                console.error('[IM] 加载好友列表失败:', error);
              }
            }, 500);
          }
        });
      } catch (error) {
        this.imInitialized = false;
      }
    },

    // 监控用户登录状态变化
    watchUserLogin() {
      // 监听user对象的变化
      this.$watch(
        () => this.user,
        (newUser, oldUser) => {
          const wasLoggedIn = oldUser && oldUser.account && oldUser.account.guid;
          const isLoggedIn = newUser && newUser.account && newUser.account.guid;

          // 如果从未登录变为登录状态，初始化IM
          if (!wasLoggedIn && isLoggedIn) {
            // 延迟初始化，避免与登录流程冲突
            if (this.imInitTimer) {
              clearTimeout(this.imInitTimer);
            }

            this.imInitTimer = setTimeout(() => {
              this.initIMConnection();
            }, 1000);
          }

          // 如果从登录变为未登录，重置IM状态
          if (wasLoggedIn && !isLoggedIn) {
            this.imInitialized = false;
          }
        },
        { deep: true, immediate: true } // immediate: true 立即检查一次
      );
    },

    // 清理定时器
    clearTimers() {
      if (this.imInitTimer) {
        clearTimeout(this.imInitTimer);
        this.imInitTimer = null;
      }
    }
  },

  created() {
    // loadBMap(Constant.BAIDU_AK);

    this.loadCompanyConfig();
  },

  mounted() {
    this.loadCurrentLocation();

    // 监控用户登录状态
    this.watchUserLogin();

    // 立即检查一次，如果用户已经登录，直接初始化
    if (this.isUserLoggedIn) {
      setTimeout(() => {
        this.initIMConnection();
      }, 1500);
    }
  },

  beforeDestroy() {
    this.clearTimers();
  }
};
</script>

<style scoped lang="less"></style>